import { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, ImageIcon, Box, Loader2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export type MediaType = 'image' | 'model3d';

export interface UploadedMedia {
  url: string;
  type: MediaType;
  name: string;
}

interface MediaUploadProps {
  onUploaded: (media: UploadedMedia) => void;
  figurineSlug?: string;
  disabled?: boolean;
}

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const MODEL_TYPES = ['model/gltf-binary', 'model/gltf+json', 'application/octet-stream'];
const MODEL_EXTS = ['.glb', '.gltf'];

function detectType(file: File): MediaType {
  if (IMAGE_TYPES.includes(file.type)) return 'image';
  const lower = file.name.toLowerCase();
  if (MODEL_EXTS.some(ext => lower.endsWith(ext))) return 'model3d';
  if (MODEL_TYPES.includes(file.type)) return 'model3d';
  return 'image';
}

export default function MediaUpload({ onUploaded, figurineSlug, disabled }: MediaUploadProps) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (file: File) => {
    setError(null);
    const mediaType = detectType(file);

    if (mediaType === 'image' && !IMAGE_TYPES.includes(file.type)) {
      setError('Dozwolone: JPG, PNG, WEBP, GIF, GLB, GLTF'); return;
    }
    if (file.size > 30 * 1024 * 1024) {
      setError('Plik za duży (max 30 MB dla modeli 3D, 10 MB dla zdjęć)'); return;
    }
    if (mediaType === 'image' && file.size > 10 * 1024 * 1024) {
      setError('Zdjęcie za duże (max 10 MB)'); return;
    }

    setUploading(true);
    setProgress(20);

    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'bin';
    const slug = figurineSlug || `figurine-${Date.now()}`;
    const prefix = mediaType === 'model3d' ? 'model' : 'img';
    const path = `${slug}/${prefix}-${Date.now()}.${ext}`;

    setProgress(40);

    const { error: uploadError } = await supabase.storage
      .from('figurine-images')
      .upload(path, file, { upsert: false, contentType: file.type });

    if (uploadError) {
      setError(`Błąd uploadu: ${uploadError.message}`);
      setUploading(false); setProgress(0); return;
    }

    setProgress(85);
    const { data } = supabase.storage.from('figurine-images').getPublicUrl(path);
    setProgress(100);

    setTimeout(() => {
      onUploaded({ url: data.publicUrl, type: mediaType, name: file.name });
      setUploading(false); setProgress(0);
    }, 300);
  }, [figurineSlug, onUploaded]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  }, [upload]);

  return (
    <label
      className="relative block cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200"
      style={{
        borderColor: dragging ? 'var(--primary)' : 'oklch(0.28 0.05 280)',
        background: dragging ? 'oklch(0.65 0.32 280 / 0.07)' : 'oklch(0.14 0.028 280)',
        pointerEvents: disabled || uploading ? 'none' : 'auto',
        opacity: disabled ? 0.5 : 1,
      }}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,.glb,.gltf"
        onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
        className="sr-only"
        disabled={disabled || uploading}
      />
      <div className="flex flex-col items-center justify-center py-5 px-3 text-center gap-2">
        {uploading ? (
          <>
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <div className="w-full max-w-[140px] h-1.5 rounded-full overflow-hidden" style={{ background: 'oklch(0.22 0.04 280)' }}>
              <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg,var(--primary),var(--accent))' }}
                animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
            </div>
            <span className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{progress}%</span>
          </>
        ) : (
          <>
            <div className="flex gap-2">
              <ImageIcon className="w-5 h-5" style={{ color: dragging ? 'var(--primary)' : 'var(--muted-foreground)' }} />
              <Box className="w-5 h-5" style={{ color: dragging ? 'var(--accent)' : 'var(--muted-foreground)' }} />
            </div>
            <p className="text-xs font-semibold text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              {dragging ? 'Upuść plik' : '+ Dodaj zdjęcie lub model 3D'}
            </p>
            <p className="text-xs text-muted-foreground leading-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              JPG · PNG · WEBP<br />GLB · GLTF (Meshy AI)
            </p>
          </>
        )}
      </div>
      {error && (
        <div className="px-3 pb-3">
          <p className="text-xs text-destructive flex items-center gap-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            <X className="w-3 h-3" />{error}
          </p>
        </div>
      )}
    </label>
  );
}
